import React, {useContext, useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';

import {useStyles} from "./AdminDashboardSection.css";
import {AppbarContentContext} from "../../../../../commons/routing/appbar/AppbarContentProvider";
import {FormattedMessage, useIntl} from 'react-intl';
import useFetchAdminDashboardData from "./useFetchAdminDashboardData";
import LoadingProgressIndication from "../../../../../commons/elements/progress/loading/LoadingProgressIndication";
import {Divider, IconButton, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography} from '@mui/material';
import FormControl from "@mui/material/FormControl";
import noDashboardData from 'assets/noDashboardData.svg';
import {FirebaseAuthContext} from "../../../../../commons/firebase/FirebaseAuthProvider";
import {BarChart, DateRangeSharp, Female, Group, Male, VideoLibrary} from "@mui/icons-material";
import StatisticsDateRangePicker from "../../../statistics/utils/StatisticsDateRangePicker";
import {useConfirm} from "material-ui-confirm";
import {subDays} from 'date-fns';
import AppbarActionButton from "../../../../../commons/elements/buttons/AppbarActionButton";
import {NavLink} from "react-router-dom";
import BasicChart from "../../../statistics/chart/BasicChart";
import ScoredScenesPerformanceList from "../../panels/performanceList/ScoredScenesPerformanceList";
import ValueIndicator from "../../panels/valueIndication/ValueIndicator";
import ValueIndicationList from "../../panels/valueIndicationList/ValueIndicationList";

export default function AdminDashboardSection() {
    const classes = useStyles();

    // Setting the current appbar content
    const {setCurrentAppbarContent} = useContext(AppbarContentContext)

    // Confirmation dialog for selecting the time range
    const confirm = useConfirm();

    // Get permission-based limited association structure to fill the association filter dropdown
    const {allowedAssociationStructure} = useContext(FirebaseAuthContext);

    // Internationalization for simple strings
    const intl = useIntl();

    // Hook for fetching the data required for rendering the statistics
    const {fetchAdminDashboardData} = useFetchAdminDashboardData()

    // Filter
    const [associationFilter, setAssociationFilter] = useState(Object.keys(allowedAssociationStructure)[0] || '')
    const [fromToDateRange, setFromToDateRange] = React.useState([subDays(new Date(), 365), new Date()])

    // Selectable metrics for time chart and worst rated
    const [timeChartMetric, setTimeChartMetric] = React.useState('trainingIntensity');
    const [worstRatedMetric, setWorstRatedMetric] = React.useState('decisionAccuracy');

    // Raw dashboard data object as basis to render charts and numbers
    const [dashboardData, setDashboardData] = useState({})

    // Loading indication - set to false in case all charts are ready
    const [loadingStatistics, setLoadingStatistics] = useState(true)


    // Setting the appbar content including the filters
    useEffect(() => {
        setCurrentAppbarContent({
            header: <FormattedMessage id={'app.dashboard.overview.appbar.header'}
                                      defaultMessage={'Dashboard'}/>,
            description: <FormattedMessage id={'app.dashboard.overview.appbar.description'}
                                           defaultMessage={'All important KPIs at one glance...'}/>,
            actions: <>
                {getFilterSelection()}
            </>
        })
    }, [associationFilter, fromToDateRange, loadingStatistics])

    // Automatically calculate statistics on filter change
    useEffect(() => {

        // Reset tab configs to initial state
        setTimeChartMetric('trainingIntensity')
        setWorstRatedMetric('decisionAccuracy')

        reloadDashboardData()
    }, [associationFilter, fromToDateRange])

    // Query statistical data and fill the chart canvas accordingly
    const reloadDashboardData = async () => {
        setLoadingStatistics(true)

        // Load the basic statistical data as a basis for feeding the charts in the dashboard
        const dashboardData = await fetchAdminDashboardData(associationFilter, fromToDateRange[0], fromToDateRange[1])

        // Set the dashboard data state, so that it can be used by the charts - go sure that the dashboard data is never set to null to avoid object access problems
        setDashboardData(dashboardData || {})

        setLoadingStatistics(false)
    }

    // Change of metric toggle button for line chart
    const handleChangeMetricForLineChart = (event, value) => {
        // Go sure that exactly one toggle button is always active
        if (value !== null) {
            setTimeChartMetric(value)
        }
    }

    // Change of metric toggle button for worst rated scored scenes
    const handleChangeMetricForWorstRatedScoredScenes = (event, value) => {
        // Go sure that exactly one toggle button is always active
        if (value !== null) {
            setWorstRatedMetric(value)
        }
    }

    const getFilterSelection = () => {

        // Hide dropdown in case of loading
        if (loadingStatistics) {
            return <></>
        }

        // Filter row
        return <div className={classes.filterRow}>

            {/* Association */}
            <FormControl className={classes.formControl}>
                <Select id="reg-course-association-input"
                        style={{color: "white", borderRadius: 30, borderColor: "grey"}}
                        variant="outlined"
                        value={associationFilter || ''}
                        onChange={(event) => setAssociationFilter(event.target.value)}>

                    {Object.keys(allowedAssociationStructure).map((association) =>
                        <MenuItem key={association} value={association}>
                            {intl.formatMessage({
                                id: `app.ids.association.${association}`
                            })}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            {/* Date range */}
            <div style={{marginLeft: 10}}>
                <b>{intl.formatDate(fromToDateRange[0]) + ' - ' + intl.formatDate(fromToDateRange[1])}</b>
                <IconButton onClick={handlePickDate} size="large">
                    <DateRangeSharp/>
                </IconButton>
            </div>

            <Divider className={classes.divider} orientation="vertical" flexItem/>

            {/* Custom statistics */}
            <div style={{marginLeft: 10}}>
                <AppbarActionButton component={NavLink}
                                    to="/admin/statistics"
                                    size="large"
                                    icon={<BarChart/>}
                                    label={intl.formatMessage({
                                        id: 'app.dashboard.overview.appbar.actions.moreStatistics',
                                        defaultMessage: 'More Statistics'
                                    })}/>
            </div>
        </div>
    }

    // Selection of time range
    const handlePickDate = () => {
        let selectedFromDate
        let selectedToDate

        confirm({
            title: intl.formatMessage({
                id: 'app.dashboard.dialog.dateRange.selection.title',
                defaultMessage: 'Choose time range...'
            }),
            content: <StatisticsDateRangePicker
                startDate={fromToDateRange[0]}
                endDate={fromToDateRange[1]}
                onPeriodChanged={(startDate, endDate) => {
                    selectedFromDate = startDate
                    selectedToDate = endDate
                }
                }/>
        }).then(() => {

            // IMPORTANT: When selecting nothing, but clicking simply on OK, selectedStartDate and selectedEndDate is undefined
            // IMPORTANT: the timestamps are always at "start date 00:00:00 UTC" - "end date 23:59:59 UTC"
            if (selectedFromDate && selectedToDate) {
                setFromToDateRange([selectedFromDate, selectedToDate])
            }
        }).catch(() => null)
    }

    // Source: Idea from https://stackoverflow.com/questions/36098913/convert-seconds-to-days-hours-minutes-and-seconds
    const formatDuration = (seconds) => {
        const h = Math.floor(seconds / 3600)
        const m = Math.floor(seconds % 3600 / 60)
        const s = Math.floor(seconds % 3600 % 60)

        const hh = h > 0 ? (h > 9 ? h : ("0" + h)) : "00"
        const mm = m > 0 ? (m > 9 ? m : ("0" + m)) : "00"
        const ss = s > 0 ? (s > 9 ? s : ("0" + s)) : "00"
        return `${hh}:${mm}:${ss}`
    }

    return (
        <div className={classes.root}>

            {/* Only hide the loading indication, so that the canvas-elements are always available in the DOM */}
            <div hidden={!loadingStatistics}>
                <LoadingProgressIndication text={intl.formatMessage({
                    id: 'app.dashboard.loading',
                    defaultMessage: 'Loading Dashboard...'
                })}/>
            </div>

            {/* Hide complete section while loading and in case no statistics are generated yet */}
            <Grid container spacing={2}
                  sx={(loadingStatistics || dashboardData.noDataYet) && {display: 'none'}}>

                {/* Overall indicators */}
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.decisionAccuracy',
                            defaultMessage: 'Correct Decisions'
                        })}
                        value={`${dashboardData.numberDecisionAccuracyTotal}%`}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.reactionTime',
                            defaultMessage: 'Reaction Time'
                        })}
                        value={dashboardData.numberReactionTimeTotal
                            ? `${dashboardData.numberReactionTimeTotal}s`
                            : '-'}/>
                </Grid>
                <Grid item xs={4}>
                    <ValueIndicator
                        size="big"
                        header={intl.formatMessage({
                            id: 'app.dashboard.overview.total.trainingIntensity',
                            defaultMessage: 'Training Intensity'
                        })}
                        value={formatDuration(dashboardData.numberTrainingIntensityTotal * 60)}/>
                </Grid>

                {/* Average bar charts */}
                <Grid item xs={4}>
                    <BasicChart chartId="chartDecisionAccuracyAvgByDisciplines"
                                chartData={dashboardData.chartDecisionAccuracyAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartReactionTimeAvgByDisciplines"
                                chartData={dashboardData.chartReactionTimeAvg}/>
                </Grid>
                <Grid item xs={4}>
                    <BasicChart chartId="chartTrainingIntensityAbsByDisciplines"
                                chartData={dashboardData.chartTrainingIntensityAbs}/>
                </Grid>

                {/* Accumulated line charts with tab selection to choose metric */}
                <Grid item xs={5}>

                    <Paper className={classes.groupPaper}>

                        {/* Selection of the metric for the accumulated line chart (depending on that charts will be shown/hidden) */}
                        <ToggleButtonGroup className={classes.centeredRow}
                                           value={timeChartMetric}
                                           exclusive
                                           onChange={handleChangeMetricForLineChart}
                                           aria-label="chart-type">

                            <ToggleButton className={classes.toggleButton}
                                          value="trainingIntensity"
                                          aria-label="trainingIntensity">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.trainingIntensity',
                                    defaultMessage: 'Training Intensity'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="decisionAccuracy"
                                          aria-label="decisionAccuracy">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.decisionAccuracy',
                                    defaultMessage: 'Decision Accuracy'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="reactionTime"
                                          aria-label="reactionTime">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.lineChart.metric.reactionTime',
                                    defaultMessage: 'Reaction Time'
                                })}
                            </ToggleButton>

                        </ToggleButtonGroup>

                        {/* Accumulated line charts (only one of them is visible) */}
                        <div>
                            <div hidden={timeChartMetric !== 'trainingIntensity'}>
                                <BasicChart chartId="chartTrainingIntensityDevByDisciplines"
                                            chartData={dashboardData.chartTrainingIntensityDev}/>
                            </div>
                            <div hidden={timeChartMetric !== 'decisionAccuracy'}>
                                <BasicChart chartId="chartDecisionAccuracyDevByDisciplines"
                                            chartData={dashboardData.chartDecisionAccuracyDev}/>
                            </div>
                            <div hidden={timeChartMetric !== 'reactionTime'}>
                                <BasicChart chartId="chartReactionTimeDevByDisciplines"
                                            chartData={dashboardData.chartReactionTimeDev}/>
                            </div>
                        </div>

                    </Paper>

                </Grid>

                {/* Worst rated scored scenes (shown in an accordion, allowing to open only one item) */}
                <Grid item xs={5}>

                    <Paper className={classes.groupPaperWorstRated}>

                        {/* Selection of the metric for the worst rated scored scenes (depending on that lists will be shown/hidden) */}
                        <ToggleButtonGroup className={classes.centeredRow}
                                           value={worstRatedMetric}
                                           exclusive
                                           onChange={handleChangeMetricForWorstRatedScoredScenes}
                                           aria-label="worst-rated-scored-scenes">

                            <ToggleButton className={classes.toggleButton} value="decisionAccuracy"
                                          aria-label="decisionAccuracy">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.decisionAccuracy',
                                    defaultMessage: 'Decision Accuracy'
                                })}
                            </ToggleButton>

                            <ToggleButton className={classes.toggleButton} value="reactionTime"
                                          aria-label="reactionTime">
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.reactionTime',
                                    defaultMessage: 'Reaction Time'
                                })}
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Worst rated list - decision accuracy */}
                        <div hidden={worstRatedMetric !== 'decisionAccuracy'}>
                            <ScoredScenesPerformanceList
                                metric="decisionAccuracy"
                                unit="%"
                                headerPreview={intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.decisionAccuracy.header',
                                    defaultMessage: 'Worst Decision Accuracy Video Scenes'
                                })}
                                listPreview={dashboardData.listWorstRatedScoredScenesDecisionAccuracy}
                                headerMore={intl.formatMessage({
                                    id: 'app.dashboard.dialog.dateRange.moreWorstRatedScenes.title',
                                    defaultMessage: 'Worst Rated - 25%'
                                })}
                                listMore={dashboardData.listWorstRatedScoredScenesDecisionAccuracyQ4}/>
                        </div>

                        {/* Worst rated list - reaction time */}
                        <div hidden={worstRatedMetric !== 'reactionTime'}>
                            <ScoredScenesPerformanceList
                                metric="reactionTime"
                                unit="s"
                                headerPreview={intl.formatMessage({
                                    id: 'app.dashboard.overview.worstScenes.metric.reactionTime.header',
                                    defaultMessage: 'Worst Reaction Time Video Scenes'
                                })}
                                listPreview={dashboardData.listWorstRatedScoredScenesReactionTime}
                                headerMore={intl.formatMessage({
                                    id: 'app.dashboard.dialog.dateRange.moreWorstRatedScenes.title',
                                    defaultMessage: 'Worst Rated - 25%'
                                })}
                                listMore={dashboardData.listWorstRatedScoredScenesReactionTimeQ4}/>

                        </div>
                    </Paper>

                </Grid>

                {/* Challenged decisions (all open vs. open resolvable by signed-in user) */}
                <Grid item xs={2}>
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/challengedDecisions`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.challengedDecisions.header',
                                    defaultMessage: 'Open Challenges'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsResolvable}/>

                        {/* Number of total open challenged decisions of the selected association */}
                        <ValueIndicator
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.challengedDecisions.metric.open.total',
                                defaultMessage: 'Total'
                            })}
                            value={dashboardData.numberOpenChallengedDecisionsTotal}/>

                    </Paper>

                    {/* Rejected scored scenes (all rejected vs. rejected resolvable by signed-in user) */}
                    <Paper className={classes.groupPaper50}>

                        {/* Header */}
                        <div className={classes.smallPanelHeader}>
                            <NavLink to={`/admin/videos`}>
                                {intl.formatMessage({
                                    id: 'app.dashboard.overview.rejectedScoredScenes.header',
                                    defaultMessage: 'Rejected Scenes'
                                })}
                            </NavLink>
                        </div>

                        {/* Number of total open challenged decisions of the selected association, where the signed-in user is the owner of the scored scene */}
                        <ValueIndicator
                            highlighted={true}
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.rejectedScoredScenes.metric.open.assignedToYou',
                                defaultMessage: 'Assigned to you'
                            })}
                            value={dashboardData.numberRejectedScoredScenesResolvable}/>

                        {/* Number of total open challenged decisions of the selected association */}
                        <ValueIndicator
                            size="small"
                            header={intl.formatMessage({
                                id: 'app.dashboard.overview.rejectedScoredScenes.metric.open.total',
                                defaultMessage: 'Total'
                            })}
                            value={dashboardData.numberRejectedScoredScenesTotal}/>

                    </Paper>

                </Grid>

                {/* Total numbers of created users grouped by discipline (including an "All" discipline) */}
                <Grid item xs={6}>
                    <ValueIndicationList
                        headerText={intl.formatMessage({
                            id: 'app.dashboard.overview.createdUsers.header',
                            defaultMessage: 'Users'
                        })}
                        headerIcon={<Group fontSize="large"/>}
                        labelValueList={dashboardData.listCreatedUsersGroupedByDiscipline}/>
                </Grid>

                {/* Total numbers of created scored scenes grouped by discipline (including an "All" discipline) */}
                <Grid item xs={6}>
                    <ValueIndicationList
                        headerText={intl.formatMessage({
                            id: 'app.dashboard.overview.createdScoredScenes.header',
                            defaultMessage: 'Scenes'
                        })}
                        headerIcon={<VideoLibrary fontSize="large"/>}
                        labelValueList={dashboardData.listCreatedScoredScenesGroupedByDiscipline}
                        secondaryValueSelectors={['countMale', 'countFemale']}
                        secondaryIcons={[<Male/>, <Female/>]}
                        secondaryColors={['#1C92D8', '#F06C6B']}/>
                </Grid>

            </Grid>

            {/* In case no statistical data has been generated yet - show indication with nice cartoon image */}
            <div hidden={loadingStatistics || !dashboardData.noDataYet}>
                <Typography component="h1" variant="h4" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.header',
                        defaultMessage: 'Missing data to render dashboard...'
                    })}
                </Typography>
                <img className={classes.image} src={noDashboardData} alt='No dashboard data'/>
                <Typography component="h1" variant="h5" textAlign="center">
                    {intl.formatMessage({
                        id: 'app.dashboard.overview.noData.description',
                        defaultMessage: 'No activity for the requested filter criteria.'
                    })}
                </Typography>
            </div>
        </div>
    );
}
